import { PrinterCheck, Download } from "lucide-react"
import React, { useRef, useState } from "react"
import { Card, Col, Row, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { Button, CardBody, CardTitle, Nav, NavItem, NavLink } from "reactstrap"
import jsPDF from "jspdf"
import html2canvas from "html2canvas"
import axios from "axios"
import {
  API_URL,
  formatDateForAPI,
  handleTableRowColor,
} from "../../../components/Common/Utils"
import { showAlert } from "../../../store/reducers/alertReducer"
import IconifyIcon from "components/Shared/IconifyIcon"
import { layoutModeTypes } from "constants/layout"
import Colors from "constants/Colors"
import CustomTabs from "./CustomTabs"

const PracticalTrainingGoalCard = ({ title, data }) => {
  return (
    <Card className="mb-4 h-100 p-2 pb-0">
      <Card.Header className="position-header text-white text-center py-3">
        {title}
      </Card.Header>
      <Card.Body className="p-0">
        {data.map((item, index) => (
          <Row
            key={index}
            className={`mx-0 py-2 px-3 ${
              index < data.length - 1 ? "border-bottom" : ""
            }`}
          >
            <div className="g_between algn-items-start px-0">
              <h6 className="fw-bold">{item.label}</h6>
              <span className="fw-semibold">{item.value}</span>
            </div>
          </Row>
        ))}
      </Card.Body>
    </Card>
  )
}

const PracticalTrainingGoalsDashboard = ({
  dashboardData,
  filters,
  layoutMode,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const [activeTab, setActiveTab] = useState("SUMMARY")
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingExcel, setIsExportingExcel] = useState(false)
  const pdfRef = useRef()

  const toggleTab = tab => {
    setActiveTab(tab)
  }

  const positions = dashboardData?.job_stats?.map(job => ({
    title: language === "sa" ? job.job_title_ar : job.job_title,
    data: [
      { label: t("Target Number"), value: job.goal_count },
      {
        label: t("Total Practical Trainings"),
        value: job.total_enrolled_candidates,
      },
      {
        label: t("Total Experienced Registration"),
        value: job.experienced_candidate_count,
      },
      // {
      //   label: t("Remaining"),
      //   value: job.remaining,
      // },
      {
        label: t("Did not Book After Interview"),
        value: job.did_not_book_after_interview,
      },
      {
        label: t("Completed Practical Training (Saudi)"),
        value: job.saudi_passed_count,
      },
      {
        label: t("Completed Practical Training (Saudi)%"),
        value: job.saudi_passed_percentage,
      },
      {
        label: t("Completed Practical Training (Non Saudi)"),
        value: job.non_saudi_passed_count,
      },
      {
        label: t("Completed Practical Training (Non Saudi)%"),
        value: job.non_saudi_passed_percentage,
      },
    ],
  }))

  const headers = [
    { label: t("Position"), key: "jobTitle" },
    // { label: t("Men Loading PLAN"), key: "planMenLoading" },
    // { label: t("For Training"), key: "goalCount" },
    { label: t("Completed"), key: "trainingCount" },
    { label: t("Completion %"), key: "completion_percentage" },
    // { label: t("RE"), key: "re" },
    { label: t("Old %"), key: "experienced_percentage" },
    { label: t("New %"), key: "new_percentage" },
    { label: t("Saudi %"), key: "saudi_percentage" },
    { label: t("Non Saudi %"), key: "non_saudi_percentage" },
  ]

  const tabularData = dashboardData?.job_stats?.map(job => ({
    jobTitle: language === "sa" ? job.job_title_ar : job.job_title,
    planMenLoading: job.goal_count / 2,
    goalCount: job.goal_count,
    trainingCount: job.practical_training_passed_count,
    completion_percentage: job.completion_percentage,
    re: job.remaining,
    non_saudi_percentage: job.non_saudi_percentage,
    saudi_percentage: job.saudi_percentage,
    new_percentage: job.new_percentage,
    experienced_percentage: job.experienced_percentage,
  }))

  // Calculate Totals
  let totals = headers.reduce((acc, header) => {
    const rowToSkip = [
      "jobTitle",
      "completion_percentage",
      "non_saudi_percentage",
      "saudi_percentage",
      "new_percentage",
      "experienced_percentage",
    ]

    if (!rowToSkip.includes(header.key)) {
      acc[header.key] = tabularData?.reduce(
        (sum, row) => sum + (row[header.key] || 0),
        0
      )
    }

    return acc
  }, {})

  headers.reduce((acc, header) => {
    if (header.key === "completion_percentage") {
      totals[header.key] =
        (totals["trainingCount"] / (totals["goalCount"] || Infinity)) * 100 || 0
    }

    if (header.key === "experienced_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["experienced_candidate_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }
    if (header.key === "new_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["new_candidates_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }
    if (header.key === "saudi_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["saudi_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }
    if (header.key === "non_saudi_percentage") {
      let currentSum = dashboardData?.job_stats?.reduce(
        (sum, row) => sum + (row["non_saudi_count"] || 0),
        0
      )

      totals[header.key] =
        (currentSum / (totals["trainingCount"] || 1)) * 100 || 0
    }

    return acc
  }, {})

  const formatNumber = num => {
    if (typeof num === "number" && !Number.isInteger(num)) {
      return num.toFixed(2)
    }
    return num
  }

  const exportAsPDF = async () => {
    setIsExporting(true)

    try {
      const pdf = new jsPDF("p", "mm", "a4")
      const pageWidth = 210 // A4 width in mm
      const pageHeight = 297 // A4 height in mm
      const margin = 10 // margin in mm
      const contentWidth = pageWidth - margin * 2

      // Get all the page sections
      const pageSections = pdfRef.current.querySelectorAll(
        '[data-page-section="true"]'
      )

      // For each section
      for (let i = 0; i < pageSections.length; i++) {
        const section = pageSections[i]

        // Add new page if not the first section
        if (i > 0) {
          pdf.addPage()
        }

        // Convert this section to canvas
        const canvas = await html2canvas(section, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        })

        const imgData = canvas.toDataURL("image/png")

        // Calculate dimensions while preserving aspect ratio
        const imgWidth = contentWidth
        const imgHeight = (canvas.height * imgWidth) / canvas.width

        // If this section fits on one page
        if (imgHeight <= pageHeight - margin * 2) {
          // Add image centered on the page
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
        }
        // If this section needs multiple pages
        else {
          let heightLeft = imgHeight
          let position = 0
          let pageCount = 0

          // Add first page of this section
          pdf.addImage(imgData, "PNG", margin, margin, imgWidth, imgHeight)
          heightLeft -= pageHeight - margin * 2

          // Add additional pages as needed for this section
          while (heightLeft > 0) {
            pageCount++
            position = -pageHeight * pageCount + margin // Position adjustment
            pdf.addPage()
            pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight)
            heightLeft -= pageHeight - margin * 2
          }
        }
      }

      pdf.save("DashboardReport.pdf")
    } catch (error) {
      console.error("PDF export failed:", error)
    } finally {
      setIsExporting(false)
    }
  }

  const trainerHeaders = [
    { label: t("ID"), key: "trainer_id" },
    { label: t("Name"), key: "full_name" },
    { label: t("Enrolled"), key: "count" },
    { label: t("Passed"), key: "count" },
    { label: t("Passed") + " %", key: "percentage" },
  ]

  const tabs = [
    { id: "SUMMARY", label: t("Summary") },
    { id: "TRAINING_SUMMARY", label: t("Training Summary") },
    { id: "GRID", label: t("Detailed View") },
    { id: "TRAINER_INFO", label: t("Trainer Statistics") },
  ]

  const totalCount = dashboardData?.trainer_stats?.reduce(
    (sum, trainer) => sum + trainer.count,
    0
  )

  const handleExport = async () => {
    try {
      setIsExportingExcel(true)
      const authUserString = localStorage.getItem("authUser")
      const authUser = authUserString ? JSON.parse(authUserString) : null
      const token = authUser?.token

      const {
        generalStatistic,
        city_ids,
        is_saudi,
        gender,
        ageGroup,
        education_qualification_id,
        dateType,
        dateRange,
      } = filters

      const payload = {
        type:
          generalStatistic === "theoretical_training_statistics_v2"
            ? "theoretical_training_statistics"
            : generalStatistic,
        // filters: {
        //   city_ids: city_ids.map(city => city.value),
        //   is_saudi,
        //   gender,
        //   education_qualification_ids: education_qualification_id.map(
        //     eduId => eduId.value
        //   ),
        //   ageGroup,
        //   dateFilter: {
        //     type: dateType,
        //     start: formatDateForAPI(dateRange.start),
        //     end: formatDateForAPI(dateRange.end),
        //   },
        // },
      }

      const response = await axios.post(
        `${API_URL}/api/v1/admin/dashboard/export`,
        payload,
        {
          headers: {
            Authorization: token, // Attach token in headers
          },
          responseType: "blob", // Important for file downloads
        }
      )

      const contentType = response.headers["content-type"]

      if (contentType === "application/json") {
        return dispatch(
          showAlert({
            text: t("Export initiated, you will be notified once done."),
            type: "success",
          })
        )
      }

      // Create a URL for the downloaded file
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", "Practical Training Dashboard.xlsx")
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error("Export failed:", error.response?.data || error.message)
    } finally {
      setIsExportingExcel(false)
    }
  }

  return (
    <Row className="employee_goal_row">
      <Col md={6} sm={12} className="mb-4">
        <div className="d-flex align-items-center gap-2">
          <IconifyIcon
            icon="octicon:goal-16"
            width={24}
            height={24}
            style={{
              color:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.primary
                  : Colors.primaryLight,
            }}
          />
          <CardTitle
            style={{
              color:
                layoutMode === layoutModeTypes.LIGHT
                  ? Colors.black
                  : Colors.white,
            }}
            className="m-0"
          >
            {t("Recruitment goal")}
          </CardTitle>
        </div>
        <p
          className="my-2"
          style={{
            color:
              layoutMode === layoutModeTypes.LIGHT
                ? Colors.mutedBlue
                : Colors.white,
          }}
        >
          {t("Statistics on employment goals and what has been achieved")}
        </p>
      </Col>

      <Col
        md={6}
        sm={12}
        className="d-flex align-items-center justify-content-end gap-4 mb-4"
      >
        <IconifyIcon
          icon="bi:filetype-xls"
          width={32}
          height={32}
          style={{
            color:
              layoutMode === layoutModeTypes.LIGHT
                ? Colors.primary
                : Colors.white,
          }}
          onClick={handleExport}
        />
        <IconifyIcon
          icon="solar:file-download-linear"
          width={32}
          height={32}
          onClick={exportAsPDF}
          style={{
            color:
              layoutMode === layoutModeTypes.LIGHT
                ? Colors.primary
                : Colors.white,
          }}
        />
      </Col>
      <Col lg={12}>
        <CustomTabs tabs={tabs} activeTab={activeTab} onChange={toggleTab} />
      </Col>

      {activeTab === "SUMMARY" || activeTab === "TRAINING_SUMMARY" ? (
        <Col lg={12} ref={pdfRef}>
          <Card data-page-section="true">
            <CardBody>
              <Table bordered responsive>
                <thead
                  style={{
                    backgroundColor:
                      layoutMode === layoutModeTypes.LIGHT
                        ? Colors.primaryLight
                        : Colors.primary,
                  }}
                >
                  <tr>
                    {headers
                      .filter(header => {
                        if (activeTab === "SUMMARY") return true
                        else if (activeTab === "TRAINING_SUMMARY") {
                          if (
                            header.key !== "planMenLoading" &&
                            header.key !== "completion_percentage"
                          ) {
                            return true
                          }
                          return false
                        } else return false
                      })
                      .map((header, idx) => (
                        <th key={idx}>
                          <div
                            style={{
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.primary
                                  : Colors.primaryLight,
                              fontSize: "12px",
                            }}
                          >
                            {header.label}
                          </div>
                        </th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {tabularData?.map((job, index) => (
                    <tr key={index}>
                      {headers
                        .filter(header => {
                          if (activeTab === "SUMMARY") return true
                          else if (activeTab === "TRAINING_SUMMARY") {
                            if (
                              header.key !== "planMenLoading" &&
                              header.key !== "completion_percentage"
                            ) {
                              return true
                            }
                            return false
                          } else return false
                        })
                        .map((header, idx) => (
                          <td
                            key={idx}
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                            className={header.key === "re" ? "text-danger" : ""}
                          >
                            {formatNumber(job[header.key])}
                          </td>
                        ))}
                    </tr>
                  ))}
                  {/* Total Row */}
                  <tr>
                    <td>
                      <strong>{t("Total")}</strong>
                    </td>
                    {headers
                      .slice(1)
                      .filter(header => {
                        if (activeTab === "SUMMARY") return true
                        else if (activeTab === "TRAINING_SUMMARY") {
                          if (
                            header.key !== "planMenLoading" &&
                            header.key !== "completion_percentage"
                          ) {
                            return true
                          }
                          return false
                        } else return false
                      })
                      .map((header, idx) => (
                        <td
                          key={idx}
                          className={
                            header.key === "re"
                              ? "text-danger fw-bold"
                              : "fw-bold"
                          }
                        >
                          {formatNumber(totals[header.key])}
                        </td>
                      ))}
                  </tr>
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      ) : activeTab === "GRID" ? (
        <div ref={pdfRef}>
          <Row data-page-section="true">
            {positions?.map((position, index) => (
              <Col
                key={index}
                xs={12}
                md={6}
                lg={3}
                className="mb-3 employee_col"
              >
                <PracticalTrainingGoalCard
                  title={position.title}
                  data={position.data}
                />
              </Col>
            ))}
          </Row>
        </div>
      ) : activeTab === "TRAINER_INFO" ? (
        <Card>
          <CardBody>
            <div ref={pdfRef}>
              <div className="table-responsive" data-page-section="true">
                <Table bordered responsive>
                  <thead
                    style={{
                      backgroundColor:
                        layoutMode === layoutModeTypes.LIGHT
                          ? Colors.primaryLight
                          : Colors.primary,
                    }}
                  >
                    <tr>
                      {trainerHeaders.map((header, idx) => (
                        <th key={idx}>
                          <div
                            style={{
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.primary
                                  : Colors.primaryLight,
                            }}
                          >
                            {header.label}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {dashboardData?.trainer_stats?.map((trainer, index) => {
                      const percentage = (
                        (trainer.passed / trainer.enrolled) *
                        100
                      )?.toFixed(2)
                      return (
                        <tr key={trainer.trainer_id}>
                          <td
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                          >
                            {trainer.trainer_id}
                          </td>
                          <td
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                          >
                            {language === "sa"
                              ? trainer.full_name_ar
                              : trainer.full_name}
                          </td>
                          <td
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                          >
                            {trainer?.enrolled}
                          </td>
                          <td
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                          >
                            {trainer?.passed}
                          </td>
                          <td
                            style={{
                              ...handleTableRowColor(layoutMode, index),
                              color:
                                layoutMode === layoutModeTypes.LIGHT
                                  ? Colors.black
                                  : Colors.white,
                            }}
                          >
                            {percentage} %
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        ""
      )}
    </Row>
  )
}

export default PracticalTrainingGoalsDashboard
